import { useState } from "react";
import { istTime } from "../../domain/istTime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  PIN_BUYER_LINK_LABEL,
  acceptPinSeller,
  invitePinBuyer,
  linkActions,
  listPinBuyers,
  listPinSellers,
  namespaceError,
  revokePinBuyer,
  revokePinSeller,
  type PinBuyerLink,
} from "../../api/pinBuyerLinks";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const when = istTime;
const statusColour = (l: PinBuyerLink) => (l.status === "accepted" ? "success" : l.status === "invited" ? "warning" : "default");

/**
 * Pin buyers (owner 2026-10-10): a licensed ISP or VNO adds another licensed
 * operator -- typically a VNO with a "mixed bag" -- that buys its pins without
 * being its sub-operator. The buyer must accept. Shown on the seller's Pin
 * plans page; a linked buyer sees only plans offered to ALL providers.
 */
export function PinBuyersSection({ seller }: { seller: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<PinBuyerLink | null>(null);
  const links = useQuery({
    queryKey: ["pinbuyers", seller],
    enabled: Boolean(identity.token && seller),
    queryFn: ({ signal }) => listPinBuyers(identity, seller, signal),
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["pinbuyers", seller] });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">Other operators buying your pins</Typography>
          <Typography variant="body2" color="text.secondary">
            A licensed ISP or VNO that is not your sub-operator can buy your pins once you add it and it accepts. It sees only plans
            offered to all your PIN providers, and you invoice it for every order.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setAdding(true)}>
          Add pin buyer
        </Button>
      </Box>
      {links.error && <Alert severity="error">{errText(links.error)}</Alert>}
      {links.data && links.data.length === 0 && <Alert severity="info">No other operator buys your pins yet.</Alert>}
      {links.data && links.data.length > 0 && (
        <LinkTable links={links.data} party="buyer" onEnd={setEnding} />
      )}
      {adding && <AddBuyerDialog seller={seller} onClose={() => setAdding(false)} onDone={() => { refresh(); setAdding(false); }} />}
      {ending && (
        <EndLinkDialog
          title={`Stop selling pins to ${ending.buyer}`}
          note="Pins already issued stay with the buyer. Only new orders stop."
          run={(reason) => revokePinBuyer(identity, seller, ending.buyer, reason)}
          onClose={() => setEnding(null)}
          onDone={() => { refresh(); setEnding(null); }}
        />
      )}
    </Box>
  );
}

/**
 * Pin sellers: the ISPs and VNOs that added this operator as a pin buyer.
 * Accept an invitation to see the seller's plans under "Buy pins".
 */
export function PinSellersSection({ buyer }: { buyer: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [ending, setEnding] = useState<PinBuyerLink | null>(null);
  const links = useQuery({
    queryKey: ["pinsellers", buyer],
    enabled: Boolean(identity.token && buyer),
    queryFn: ({ signal }) => listPinSellers(identity, buyer, signal),
    retry: false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["pinsellers", buyer] });
    void qc.invalidateQueries({ queryKey: ["pinoffer", buyer] });
  };
  const accept = useMutation({
    mutationFn: (seller: string) => acceptPinSeller(identity, buyer, seller),
    onSuccess: refresh,
  });

  if (links.data && links.data.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="subtitle1">Operators selling pins to you</Typography>
      <Typography variant="body2" color="text.secondary">
        Accept to buy their pins. You keep selling your own plans, in your own name.
      </Typography>
      {links.error && <Alert severity="error">{errText(links.error)}</Alert>}
      {accept.error && <Alert severity="error">{errText(accept.error)}</Alert>}
      {links.data && (
        <LinkTable
          links={links.data}
          party="seller"
          onAccept={(l) => accept.mutate(l.seller)}
          accepting={accept.isPending}
          onEnd={setEnding}
        />
      )}
      {ending && (
        <EndLinkDialog
          title={ending.status === "invited" ? `Decline ${ending.seller}` : `Stop buying pins from ${ending.seller}`}
          note="Pins you already hold stay yours. Only new orders stop."
          run={(reason) => revokePinSeller(identity, buyer, ending.seller, reason)}
          onClose={() => setEnding(null)}
          onDone={() => { refresh(); setEnding(null); }}
        />
      )}
    </Box>
  );
}

function LinkTable({
  links,
  party,
  onAccept,
  accepting,
  onEnd,
}: {
  links: PinBuyerLink[];
  party: "buyer" | "seller";
  onAccept?: (l: PinBuyerLink) => void;
  accepting?: boolean;
  onEnd: (l: PinBuyerLink) => void;
}) {
  const side = party === "buyer" ? "seller" : "buyer";
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{party === "buyer" ? "Buyer" : "Seller"}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Since</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((l) => {
            const can = linkActions(l, side);
            return (
              <TableRow key={`${l.seller}/${l.buyer}`}>
                <TableCell>
                  <code>{party === "buyer" ? l.buyer : l.seller}</code>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={statusColour(l)} label={PIN_BUYER_LINK_LABEL[l.status]} />
                  {l.status === "revoked" && l.revokereason && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {l.revokereason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{when(l.status === "accepted" ? l.acceptedat : l.status === "revoked" ? l.revokedat : l.invitedat)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {can.accept && onAccept && (
                      <Button size="small" variant="contained" disabled={accepting} onClick={() => onAccept(l)}>
                        Accept
                      </Button>
                    )}
                    {can.revoke && (
                      <Button size="small" color="error" onClick={() => onEnd(l)}>
                        {side === "buyer" && l.status === "invited" ? "Decline" : "End"}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AddBuyerDialog({ seller, onClose, onDone }: { seller: string; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [buyer, setBuyer] = useState("");
  const error = namespaceError(buyer);
  const add = useMutation({ mutationFn: () => invitePinBuyer(identity, seller, buyer), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add pin buyer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Operator namespace"
            value={buyer}
            onChange={(e) => setBuyer(e.target.value)}
            error={buyer !== "" && Boolean(error)}
            helperText={buyer !== "" && error ? error : "Ask the operator for its namespace, e.g. net.example"}
            fullWidth
            autoFocus
          />
          <Alert severity="info">
            It must be a licensed ISP or VNO, not anyone's sub-operator, and outside your own tree. It buys only once it accepts.
          </Alert>
          {add.error && <Alert severity="error">{errText(add.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(error) || add.isPending} onClick={() => add.mutate()}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EndLinkDialog({
  title,
  note,
  run,
  onClose,
  onDone,
}: {
  title: string;
  note: string;
  run: (reason: string) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const end = useMutation({ mutationFn: () => run(reason.trim()), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} helperText="Shown to the other operator." fullWidth autoFocus />
          <Alert severity="info">{note}</Alert>
          {end.error && <Alert severity="error">{errText(end.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={reason.trim() === "" || end.isPending} onClick={() => end.mutate()}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
